import { useState, useEffect, useRef, useCallback } from 'react';
import { Socket, Channel } from 'phoenix';

interface UseShipmentsExportReportOptions {
  userId: string;
  jwtToken: string;
  websocketUrl: string;
  onDownload?: () => void;
}

interface CacheStatus {
  status: 'cached' | 'processing' | 'none' | 'checking';
  cached_at?: string;
  age_seconds?: number;
  row_count?: number;
  presigned_url?: string;
  job_uuid?: string;
  started_at?: string;
}

interface ShipmentsExportState {
  isGenerating: boolean;
  cacheStatus: CacheStatus;
  error: string | null;
  maxRangeDays: number;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ITERATIONS = 60; // ~2 min timeout (query takes ~5s)
const DEFAULT_MAX_RANGE_DAYS = 90;

export function useShipmentsExportReport(options: UseShipmentsExportReportOptions) {
  const { userId, jwtToken, websocketUrl, onDownload } = options;

  const onDownloadRef = useRef(onDownload);
  onDownloadRef.current = onDownload;

  const [state, setState] = useState<ShipmentsExportState>({
    isGenerating: false,
    cacheStatus: { status: 'none' },
    error: null,
    maxRangeDays: DEFAULT_MAX_RANGE_DAYS,
  });

  const socketRef = useRef<Socket | null>(null);
  const channelRef = useRef<Channel | null>(null);
  const currentJobRef = useRef<string | null>(null);
  const currentParamsRef = useRef<{ facilityId: number; startDate: string; endDate: string } | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const autoDownload = useCallback((url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `shipments_export_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Initialize socket and channel
  useEffect(() => {
    if (!userId || !jwtToken) return;

    const socket = new Socket(websocketUrl, {
      params: { token: jwtToken },
    });

    socket.connect();
    socketRef.current = socket;

    const channel = socket.channel(`reports:${userId}`, {});

    channel.join()
      .receive('ok', () => {
        console.log('Connected to reports channel (shipments export)');
        // Fetch configurable max range days
        channel.push('get_report_config', { template: 'shipments_by_site' })
          .receive('ok', (response: any) => {
            if (response.max_range_days) {
              setState(prev => ({ ...prev, maxRangeDays: response.max_range_days }));
            }
          });
      })
      .receive('error', (err: any) => {
        console.error('Failed to join reports channel:', err);
        setState(prev => ({
          ...prev,
          error: 'Failed to connect to report service',
        }));
      });

    channelRef.current = channel;

    channel.on('report_completed', (payload: any) => {
      if (payload.job_uuid === currentJobRef.current) {
        clearPolling();
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: null,
        }));

        if (payload.gcs_url) {
          autoDownload(payload.gcs_url);
          onDownloadRef.current?.();
        }

        currentJobRef.current = null;

        const params = currentParamsRef.current;
        if (!params) return;
        channel.push('check_report_cache', {
          template: 'shipments_by_site',
          facility_id: params.facilityId,
          start_date: params.startDate,
          end_date: params.endDate,
        })
          .receive('ok', (response: any) => {
            setState(prev => ({
              ...prev,
              cacheStatus: {
                status: response.status || 'cached',
                cached_at: response.cached_at,
                age_seconds: response.age_seconds,
                row_count: response.row_count,
                presigned_url: response.presigned_url,
              },
            }));
          });
      }
    });

    channel.on('report_failed', (payload: any) => {
      if (payload.job_uuid === currentJobRef.current) {
        clearPolling();
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: payload.error_message || 'Report generation failed',
        }));
        currentJobRef.current = null;
      }
    });

    channel.on('report_progress', (payload: any) => {
      if (payload.job_uuid === currentJobRef.current) {
        setState(prev => ({
          ...prev,
          isGenerating: true,
        }));
      }
    });

    return () => {
      channel.leave();
      socket.disconnect();
    };
  }, [userId, jwtToken, websocketUrl, autoDownload, clearPolling]);

  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  const startPolling = useCallback((facilityId: number, startDate: string, endDate: string) => {
    clearPolling();

    pollIntervalRef.current = setInterval(() => {
      pollCountRef.current += 1;

      if (pollCountRef.current >= MAX_POLL_ITERATIONS) {
        clearPolling();
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: 'Report generation timed out. Please try again.',
        }));
        return;
      }

      const channel = channelRef.current;
      if (!channel) return;

      channel.push('check_report_cache', {
        template: 'shipments_by_site',
        facility_id: facilityId,
        start_date: startDate,
        end_date: endDate,
      })
        .receive('ok', (response: any) => {
          if (response.status === 'cached') {
            clearPolling();
            setState(prev => ({
              ...prev,
              isGenerating: false,
              cacheStatus: {
                status: 'cached',
                cached_at: response.cached_at,
                age_seconds: response.age_seconds,
                row_count: response.row_count,
                presigned_url: response.presigned_url,
              },
              error: null,
            }));
            if (response.presigned_url) {
              autoDownload(response.presigned_url);
              onDownloadRef.current?.();
            }
          }
        });
    }, POLL_INTERVAL_MS);
  }, [clearPolling, autoDownload]);

  const checkCache = useCallback((facilityId: number, startDate: string, endDate: string) => {
    const channel = channelRef.current;
    if (!channel) return;

    setState(prev => ({
      ...prev,
      cacheStatus: { status: 'checking' },
      error: null,
    }));

    channel.push('check_report_cache', {
      template: 'shipments_by_site',
      facility_id: facilityId,
      start_date: startDate,
      end_date: endDate,
    })
      .receive('ok', (response: any) => {
        setState(prev => ({
          ...prev,
          cacheStatus: {
            status: response.status,
            cached_at: response.cached_at,
            age_seconds: response.age_seconds,
            row_count: response.row_count,
            presigned_url: response.presigned_url,
            job_uuid: response.job_uuid,
            started_at: response.started_at,
          },
        }));

        if (response.status === 'processing') {
          setState(prev => ({ ...prev, isGenerating: true }));
          if (response.job_uuid) {
            currentJobRef.current = response.job_uuid;
          }
          startPolling(facilityId, startDate, endDate);
        }
      })
      .receive('error', (err: any) => {
        console.error('Failed to check report cache:', err);
        setState(prev => ({
          ...prev,
          cacheStatus: { status: 'none' },
          error: 'Failed to check report cache',
        }));
      });
  }, [startPolling]);

  const generateReport = useCallback((facilityId: number, startDate: string, endDate: string, forceRealtime: boolean) => {
    const channel = channelRef.current;
    if (!channel) {
      setState(prev => ({ ...prev, error: 'Not connected' }));
      return;
    }

    currentParamsRef.current = { facilityId, startDate, endDate };

    setState(prev => ({
      ...prev,
      isGenerating: true,
      error: null,
    }));

    channel.push('create_report', {
      template: 'shipments_by_site',
      facility_id: facilityId,
      start_date: startDate,
      end_date: endDate,
      force_realtime: forceRealtime,
    })
      .receive('ok', (response: any) => {
        if (response.status === 'completed') {
          autoDownload(response.gcs_url);
          onDownloadRef.current?.();
          setState(prev => ({ ...prev, isGenerating: false, error: null }));
        } else {
          currentJobRef.current = response.job_uuid;
          setState(prev => ({ ...prev, isGenerating: true }));
          startPolling(facilityId, startDate, endDate);
        }
      })
      .receive('error', (err: any) => {
        console.error('Failed to create shipments export:', err);
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: err.reason || err.error_message || 'Failed to create report',
        }));
      });
  }, [startPolling, autoDownload]);

  const resetState = useCallback(() => {
    clearPolling();
    currentJobRef.current = null;
    currentParamsRef.current = null;
    setState(prev => ({
      isGenerating: false,
      cacheStatus: { status: 'none' },
      error: null,
      maxRangeDays: prev.maxRangeDays,
    }));
  }, [clearPolling]);

  return {
    ...state,
    checkCache,
    generateReport,
    resetState,
  };
}
